/* eslint-disable max-lines-per-function */

/* eslint-disable complexity */
import React from 'react';

import { Button } from '@qality/common-components/src/atoms/Button';
import { Flex } from '@qality/common-components/src/atoms/Flex';
import { IssueStatus } from '@qality/common-components/src/atoms/IssueStatus';
import {
  Table,
  createColumnWidths,
  createColumnsWidths,
} from '@qality/common-components/src/atoms/Table';
import { Text } from '@qality/common-components/src/atoms/Text';
import { User } from '@qality/common-components/src/atoms/User';
import { Issue } from '@qality/common-components/src/molecules/Issue';
import { SpinnerLoader } from '@qality/common-components/src/molecules/SpinnerLoader';
import { ReportedBug } from '@qality/common-model';
import { DateFormatService } from '@qality/common-service';

import { ReportedBugsStyled } from './ReportedBugs.styled';

type Columns = 'bug' | 'status' | 'reporter' | 'created' | 'actions';

const ColumnsWidths = createColumnsWidths<Columns>({
  bug: createColumnWidths('auto', '200px'),
  status: createColumnWidths('100px'),
  reporter: createColumnWidths('200px', '120px'),
  created: createColumnWidths('200px'),
  actions: createColumnWidths('150px'),
});

type ReportedBugsViewProps = {
  bugs: Array<ReportedBug>;
  isLoading: boolean;
  openExecution: (testExecutionId: number) => void;
};

export const ReportedBugsView: React.FC<ReportedBugsViewProps> = ({
  bugs,
  openExecution,
  isLoading,
}: ReportedBugsViewProps) => (
  <ReportedBugsStyled>
    <Table>
      <Table.Head>
        <Table.Tr>
          <Table.Th widths={ColumnsWidths.bug}>Bug</Table.Th>
          <Table.Th widths={ColumnsWidths.status}>Status</Table.Th>
          <Table.Th widths={ColumnsWidths.reporter}>Reporter</Table.Th>
          <Table.Th widths={ColumnsWidths.created}>Created</Table.Th>
          <Table.Th widths={ColumnsWidths.actions}>Actions</Table.Th>
        </Table.Tr>
      </Table.Head>
      <Table.Body>
        {isLoading && (
          <Table.Tr>
            <Table.Td colSpan={6}>
              <Flex justifyCenter paddingSize='small'>
                <SpinnerLoader text='We are loading data...' />
              </Flex>
            </Table.Td>
          </Table.Tr>
        )}
        {!isLoading && bugs.length === 0 && (
          <Table.Tr>
            <Table.Td colSpan={6}>
              <Flex justifyCenter paddingSize='medium'>
                <Text>There is no data to display here</Text>
              </Flex>
            </Table.Td>
          </Table.Tr>
        )}
        {!isLoading &&
          bugs.length > 0 &&
          bugs?.map((bug, index) => {
            const { linkedIssue } = bug;
            return (
              <Table.Tr
                isClickable
                isHoverable
                key={index}
                onClick={() => openExecution(bug.testExecution.id)}
              >
                <Table.Td>
                  <Table.ContentHelper alignItems='center'>
                    <Issue
                      iconSrc={linkedIssue.iconSrc}
                      title={linkedIssue.title}
                      issueKey={linkedIssue.issueKey}
                      issueLink={linkedIssue.issueLink}
                    />
                  </Table.ContentHelper>
                </Table.Td>
                <Table.Td>
                  <Table.ContentHelper alignItems='center'>
                    <IssueStatus
                      kind={linkedIssue.status.kind}
                      spacing='compact'
                    >
                      {linkedIssue.status.text}
                    </IssueStatus>
                  </Table.ContentHelper>
                </Table.Td>
                <Table.Td>
                  <Table.ContentHelper alignItems='center'>
                    {linkedIssue.reporter ? (
                      <User
                        userName={linkedIssue.reporter.userName}
                        userAvatar={linkedIssue.reporter.userAvatar}
                        size='xsmall'
                      />
                    ) : (
                      <Text inactive>Anonymous</Text>
                    )}
                  </Table.ContentHelper>
                </Table.Td>
                <Table.Td>
                  <Table.ContentHelper alignItems='center'>
                    {linkedIssue.created ? (
                      <Text>
                        {DateFormatService.formatDate(
                          linkedIssue.created,
                          'longDateFormat'
                        )}
                      </Text>
                    ) : (
                      <Text inactive>None</Text>
                    )}
                  </Table.ContentHelper>
                </Table.Td>
                <Table.Td className='show-on-row-hover'>
                  <Table.ContentHelper alignItems='center'>
                    <Button
                      marginSize='small'
                      onClick={() => openExecution(bug.testExecution.id)}
                    >
                      Open Execution
                    </Button>
                  </Table.ContentHelper>
                </Table.Td>
              </Table.Tr>
            );
          })}
      </Table.Body>
    </Table>
  </ReportedBugsStyled>
);
